import React from "react";
import { HardHat, ClipboardCheck, Camera, ShieldAlert, Trees, Receipt } from "lucide-react";

const REMOTE_BUILD = [
  {
    icon: HardHat,
    title: "Vetted site inspections",
    body:
      "Certified, highly experienced builders and contractors conduct regular site visits on your behalf.",
  },
  {
    icon: ClipboardCheck,
    title: "Comprehensive progress reporting",
    body:
      "Detailed, transparent reports featuring high-resolution imagery and video documentation of each construction phase.",
  },
  {
    icon: Camera,
    title: "Virtual walk-throughs",
    body:
      "Scheduled live or recorded tours so overseas owners can review work in real time, from anywhere in the world.",
  },
];

const ABSENTEE = [
  {
    icon: Trees,
    title: "Grounds & maintenance",
    body:
      "Pristine property conditions during your absence — full-service lawn and landscape care, gutters, exterior upkeep.",
  },
  {
    icon: Receipt,
    title: "Financial & utility administration",
    body:
      "Timely property tax payments and ongoing utility bill processing to ensure uninterrupted service.",
  },
  {
    icon: ShieldAlert,
    title: "Surveillance & security",
    body:
      "Oversight of property security systems, including active monitoring of cameras — continuous peace of mind.",
  },
];

const FeatureItem = ({ item, chipClass }) => {
  const Icon = item.icon;
  return (
    <li className="flex gap-4">
      <span className={`flex-shrink-0 w-11 h-11 rounded-full flex items-center justify-center text-[var(--gg-primary)] ${chipClass}`}>
        <Icon size={18} strokeWidth={1.5} />
      </span>
      <div>
        <h4 className="font-serif text-lg text-[var(--gg-deep)]">{item.title}</h4>
        <p className="text-sm text-[var(--gg-text)] leading-relaxed mt-1.5">{item.body}</p>
      </div>
    </li>
  );
};

const PropertyBlock = ({ index, testId, title, sub, items, cream }) => (
  <article
    data-testid={testId}
    className={`rounded-2xl p-8 md:p-10 ${
      cream
        ? "bg-[var(--gg-cream)] border border-[var(--gg-secondary)]/40"
        : "bg-white border border-gray-100 hover:shadow-md transition-all"
    }`}
  >
    <div className="flex items-center gap-3 mb-2">
      <span className="text-[11px] tracking-[0.25em] uppercase text-[var(--gg-primary)]">
        {index}
      </span>
      <span className="h-px flex-1 bg-[var(--gg-secondary)]/50" />
    </div>
    <h3 className="font-serif text-2xl md:text-3xl text-[var(--gg-deep)] mt-3 mb-2">
      {title}
    </h3>
    <p className="text-sm text-[var(--gg-text)] leading-relaxed">{sub}</p>
    <ul className="mt-7 space-y-6">
      {items.map((it) => (
        <FeatureItem
          key={it.title}
          item={it}
          chipClass={cream ? "bg-white border border-[var(--gg-secondary)]/50" : "bg-[var(--gg-primary)]/10"}
        />
      ))}
    </ul>
  </article>
);

export default function PropertyManagementSection() {
  return (
    <section
      id="property"
      data-testid="property-section"
      className="py-28 md:py-36"
      style={{
        background:
          "linear-gradient(180deg, #FDFDFD 0%, #F1F6F7 100%)",
      }}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="max-w-3xl mb-16">
          <span className="eyebrow">Property Management</span>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[var(--gg-deep)] mt-5 leading-[1.05] text-balance">
            Eyes on the ground,
            <br />
            <em className="not-italic text-[var(--gg-primary)] font-normal">
              from anywhere in the world.
            </em>
          </h2>
          <p className="mt-7 text-lg text-[var(--gg-text)] leading-relaxed max-w-2xl">
            For diaspora homeowners and absentee owners — quiet, professional
            oversight of your Jamaican property and any ongoing construction,
            with the same dignity and discretion that defines our senior care.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-7">
          <PropertyBlock
            index="01"
            testId="property-block-construction"
            title="Remote construction monitoring & quality assurance"
            sub="Building from abroad? We act as your trusted eyes on site."
            items={REMOTE_BUILD}
          />
          <PropertyBlock
            index="02"
            testId="property-block-absentee"
            title="Absentee estate upkeep & oversight"
            sub="For homeowners who can't be there year-round."
            items={ABSENTEE}
            cream
          />
        </div>

        <div className="mt-12 text-center">
          <a
            href="#contact"
            data-testid="property-cta"
            className="inline-flex items-center gap-3 bg-[var(--gg-primary)] text-white hover:bg-[var(--gg-primary-dark)] transition-colors rounded-full pl-7 pr-3 py-3.5 text-sm"
          >
            Request a property management quote
            <span className="w-9 h-9 rounded-full bg-white/15 flex items-center justify-center">
              →
            </span>
          </a>
        </div>
      </div>
    </section>
  );
}
